import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, Star, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminTariffs() {
  const [tariffs, setTariffs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  
  const initialFormData = {
    category_id: "",
    provider_id: "",
    name: "",
    slug: "",
    price: 0,
    price_unit: "€/Monat",
    original_price: null,
    contract_duration: "24 Monate",
    bonus: "",
    bonus_amount: 0,
    rating: 4.0,
    rating_count: 0,
    features: [],
    pros: [],
    cons: [],
    affiliate_link: "",
    is_featured: false,
    is_testsieger: false,
    order: 0,
    is_active: true,
    data_volume: "",
    speed: "",
    coverage_type: "",
  };
  
  const [formData, setFormData] = useState(initialFormData);
  const [featuresText, setFeaturesText] = useState("");
  const [prosText, setProsText] = useState("");
  const [consText, setConsText] = useState("");

  const fetchData = async () => {
    try {
      const [tariffsRes, categoriesRes, providersRes] = await Promise.all([
        axios.get(`${API}/tariffs`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/providers`),
      ]);
      setTariffs(tariffsRes.data);
      setCategories(categoriesRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        features: featuresText.split("\n").filter(f => f.trim()),
        pros: prosText.split("\n").filter(p => p.trim()),
        cons: consText.split("\n").filter(c => c.trim()),
        original_price: formData.original_price || null,
      };

      if (editingTariff) {
        await axios.put(`${API}/tariffs/${editingTariff.id}`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("Tarife güncellendi");
      } else {
        await axios.post(`${API}/tariffs`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("Tarife eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu tarifeyi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/tariffs/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Tarife silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleEdit = (tariff) => {
    setEditingTariff(tariff);
    setFormData({
      ...tariff,
      original_price: tariff.original_price || "",
    });
    setFeaturesText(tariff.features?.join("\n") || "");
    setProsText(tariff.pros?.join("\n") || "");
    setConsText(tariff.cons?.join("\n") || "");
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTariff(null);
    setFormData(initialFormData);
    setFeaturesText("");
    setProsText("");
    setConsText("");
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[äÄ]/g, "ae")
      .replace(/[öÖ]/g, "oe")
      .replace(/[üÜ]/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const filteredTariffs = filterCategory === "all" 
    ? tariffs 
    : tariffs.filter(t => t.category_id === filterCategory);

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "";
  const getProviderName = (id) => providers.find(p => p.id === id)?.name || "";

  return (
    <AdminLayout title="Tarifeler">
      <div className="space-y-6" data-testid="admin-tariffs">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Kategori Filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-electric-500 hover:bg-electric-600"
            data-testid="add-tariff-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Tarife
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTariffs.map((tariff) => (
              <Card key={tariff.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-slate-600">{tariff.order}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-navy-900">{tariff.name}</h3>
                          {tariff.is_testsieger && (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Award className="w-3 h-3 mr-1" />
                              Testsieger
                            </Badge>
                          )}
                          {tariff.is_featured && (
                            <Badge className="bg-electric-100 text-electric-800">
                              <Star className="w-3 h-3 mr-1" />
                              Öne Çıkan
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {getProviderName(tariff.provider_id)} • {getCategoryName(tariff.category_id)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold text-navy-900">
                          {tariff.price.toFixed(2)} <span className="text-sm font-normal text-slate-500">{tariff.price_unit}</span>
                        </p>
                        <p className="text-xs text-slate-500">{tariff.contract_duration}</p>
                      </div>
                      
                      <span className={`px-2 py-1 text-xs rounded-full ${tariff.is_active ? "bg-signal-100 text-signal-700" : "bg-slate-100 text-slate-500"}`}>
                        {tariff.is_active ? "Aktif" : "Pasif"}
                      </span>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(tariff)}
                          data-testid={`edit-tariff-${tariff.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(tariff.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-tariff-${tariff.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTariff ? "Tarife Düzenle" : "Yeni Tarife"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kategori *</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sağlayıcı *</Label>
                  <Select 
                    value={formData.provider_id} 
                    onValueChange={(v) => setFormData({ ...formData, provider_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sağlayıcı seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((prov) => (
                        <SelectItem key={prov.id} value={prov.id}>{prov.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tarife Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: editingTariff ? formData.slug : generateSlug(e.target.value),
                      });
                    }}
                    placeholder="Vattenfall Natur12"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL)</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="vattenfall-natur12"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fiyat *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fiyat Birimi</Label>
                  <Input
                    value={formData.price_unit}
                    onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
                    placeholder="€/Monat"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eski Fiyat</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price || ""}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sözleşme Süresi</Label>
                  <Input
                    value={formData.contract_duration}
                    onChange={(e) => setFormData({ ...formData, contract_duration: e.target.value })}
                    placeholder="24 Monate"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bonus</Label>
                  <Input
                    value={formData.bonus}
                    onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                    placeholder="120€ Neukundenbonus"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Hız (Internet)</Label>
                  <Input
                    value={formData.speed}
                    onChange={(e) => setFormData({ ...formData, speed: e.target.value })}
                    placeholder="100 Mbit/s"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Veri (Mobil)</Label>
                  <Input
                    value={formData.data_volume}
                    onChange={(e) => setFormData({ ...formData, data_volume: e.target.value })}
                    placeholder="20 GB"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kapsam (Sigorta)</Label>
                  <Input
                    value={formData.coverage_type}
                    onChange={(e) => setFormData({ ...formData, coverage_type: e.target.value })}
                    placeholder="Haftpflicht"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Puan (1-5)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Değerlendirme Sayısı</Label>
                  <Input
                    type="number"
                    value={formData.rating_count}
                    onChange={(e) => setFormData({ ...formData, rating_count: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Özellikler (Her satıra bir özellik)</Label>
                <Textarea
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                  placeholder="100% Ökostrom&#10;Preisgarantie 12 Monate&#10;Keine Vorauskasse"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Artılar (Her satıra bir artı)</Label>
                  <Textarea
                    value={prosText}
                    onChange={(e) => setProsText(e.target.value)}
                    placeholder="Günstiger Preis&#10;Ökostrom"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eksiler (Her satıra bir eksi)</Label>
                  <Textarea
                    value={consText}
                    onChange={(e) => setConsText(e.target.value)}
                    placeholder="12 Monate Mindestlaufzeit"
                    rows={3}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Affiliate Link</Label>
                <Input
                  value={formData.affiliate_link}
                  onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sıra</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Aktif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                  />
                  <Label>Öne Çıkan</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_testsieger}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_testsieger: checked })}
                  />
                  <Label>Testsieger</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-electric-500 hover:bg-electric-600">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
